import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus, FaSearch } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const S = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .pj-wrap {
    min-height: 100vh;
    background: #f0f2f8;
    font-family: 'Plus Jakarta Sans', sans-serif;
    padding-top: 108px;
  }

  /* ── TOP BANNER ── */
  .pj-banner {
    background: linear-gradient(120deg, #1e1b4b 0%, #3730a3 50%, #4f46e5 100%);
    padding: 36px 48px 80px;
    position: relative;
    overflow: hidden;
  }

  .pj-banner::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 280px; height: 280px;
    border-radius: 50%;
    background: rgba(255,255,255,0.04);
  }

  .pj-banner::after {
    content: '';
    position: absolute;
    bottom: -80px; left: 30%;
    width: 360px; height: 360px;
    border-radius: 50%;
    background: rgba(255,255,255,0.03);
  }

  .pj-banner-title {
    font-size: 28px;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.6px;
  }

  .pj-banner-sub {
    margin-top: 6px;
    font-size: 14px;
    color: rgba(255,255,255,0.55);
    font-weight: 400;
  }

  .pj-stats-row {
    display: flex;
    gap: 16px;
    margin-top: 24px;
  }

  .pj-stat-pill {
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.14);
    border-radius: 40px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(255,255,255,0.85);
    display: flex;
    align-items: center;
    gap: 7px;
    backdrop-filter: blur(6px);
  }

  .pj-stat-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #818cf8;
  }

  /* ── FLOATING CONTENT PANEL ── */
  .pj-body {
    max-width: 1100px;
    margin: -44px auto 0;
    padding: 0 32px 60px;
    position: relative;
    z-index: 2;
  }

  /* ── ACTION BAR ── */
  .pj-actionbar {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(30,27,75,0.10);
    padding: 18px 22px;
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  .pj-search-wrap {
    flex: 1;
    min-width: 180px;
    position: relative;
  }

  .pj-search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #a5b4fc;
    font-size: 13px;
  }

  .pj-search {
    width: 100%;
    border: 1.5px solid #e8eaf6;
    border-radius: 10px;
    padding: 10px 14px 10px 38px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    color: #1e1b4b;
    background: #f8f8fd;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .pj-search::placeholder { color: #c7c9e8; }

  .pj-search:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    background: #fff;
  }

  .pj-add-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .pj-new-input {
    border: 1.5px solid #e8eaf6;
    border-radius: 10px;
    padding: 10px 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    color: #1e1b4b;
    background: #f8f8fd;
    outline: none;
    width: 400px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .pj-new-input::placeholder { color: #c7c9e8; }

  .pj-new-input:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    background: #fff;
  }

  .pj-btn-add {
    background: #4f46e5;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
    box-shadow: 0 3px 12px rgba(79,70,229,0.35);
    transition: all 0.18s;
  }

  .pj-btn-add:hover {
    background: #4338ca;
    box-shadow: 0 5px 18px rgba(79,70,229,0.45);
    transform: translateY(-1px);
  }

  /* ── CARD GRID ── */
  .pj-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;
  }

  .pj-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(30,27,75,0.07);
    padding: 22px 20px 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    border: 1.5px solid transparent;
    transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;
    animation: cardIn 0.3s ease both;
  }

  @keyframes cardIn {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .pj-card:hover {
    box-shadow: 0 8px 28px rgba(79,70,229,0.13);
    border-color: #e0e1ff;
    transform: translateY(-2px);
  }

  .pj-card.editing {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }

  .pj-card-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #eef2ff, #e0e7ff);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 800;
    color: #4f46e5;
    letter-spacing: -1px;
    flex-shrink: 0;
  }

  .pj-card-name {
    font-size: 15px;
    font-weight: 700;
    color: #1e1b4b;
    line-height: 1.3;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .pj-card-key {
    font-size: 11.5px;
    font-weight: 600;
    color: #a5b4fc;
    background: #eef2ff;
    border-radius: 6px;
    padding: 2px 8px;
    display: inline-block;
    margin-top: 3px;
    letter-spacing: 0.5px;
  }

  .pj-card-edit-input {
    width: 100%;
    border: 1.5px solid #6366f1;
    border-radius: 8px;
    padding: 8px 11px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    color: #1e1b4b;
    outline: none;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
    margin-bottom: 6px;
  }

  .pj-card-footer {
    display: flex;
    gap: 8px;
    padding-top: 10px;
    border-top: 1px solid #f0f1fb;
  }

  .pj-card-btn {
    flex: 1;
    border: none;
    border-radius: 8px;
    padding: 8px 0;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    transition: all 0.15s;
  }

  .pj-card-btn.edit-btn  { background: #f0f1ff; color: #4f46e5; }
  .pj-card-btn.edit-btn:hover  { background: #e0e3ff; }
  .pj-card-btn.del-btn   { background: #fff1f1; color: #ef4444; }
  .pj-card-btn.del-btn:hover   { background: #ffe0e0; }
  .pj-card-btn.save-btn  { background: #4f46e5; color: #fff; box-shadow: 0 2px 8px rgba(79,70,229,0.3); }
  .pj-card-btn.save-btn:hover  { background: #4338ca; }
  .pj-card-btn.cancel-btn { background: #f3f4f6; color: #6b7280; }
  .pj-card-btn.cancel-btn:hover { background: #e5e7eb; }

  /* ── EMPTY ── */
  .pj-empty {
    grid-column: 1 / -1;
    padding: 72px 0;
    text-align: center;
  }

  .pj-empty-emoji { font-size: 48px; margin-bottom: 14px; }
  .pj-empty-txt   { font-size: 15px; font-weight: 600; color: #818cf8; }
  .pj-empty-sub   { font-size: 13px; color: #c7c9e8; margin-top: 4px; }

  .pj-section-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #a5b4fc;
    margin-bottom: 14px;
  }

  @media (max-width: 640px) {
    .pj-banner { padding: 28px 20px 70px; }
    .pj-body   { padding: 0 14px 40px; }
    .pj-actionbar { flex-direction: column; align-items: stretch; }
    .pj-add-row   { flex-direction: column; }
    .pj-new-input { width: 100%; }
    .pj-banner-title { font-size: 22px; }
  }
`;

export default function ProjectCRUD() {
  const [projects, setProjects] = useState([]);
  const [newProject, setNewProject] = useState("");
  const [newProjectKey, setNewProjectKey] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editKey, setEditKey] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error();
      setProjects(await res.json());
    } catch { toast.error("Failed to load projects."); }
  };

  useEffect(() => { fetchProjects(); }, []);

  useEffect(() => {
    const t = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!t) { toast.error("You must be logged in to access this page."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("You are not authorized to access this page."); navigate("/"); }
  }, [navigate]);

  const handleAdd = async () => {
    if (!newProject.trim() || !newProjectKey.trim()) {
      toast.warning("Please enter project name and key.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newProject, key: newProjectKey }),
      });
      if (res.ok) {
        toast.success("Project added successfully!");
        setNewProject("");
        setNewProjectKey("");
        fetchProjects();
      } else { toast.error(await res.text()); }
    } catch { toast.error("Error adding project."); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim() || !editKey.trim()) {
      toast.warning("Enter valid project name and key.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: editName, key: editKey }),
      });
      if (res.ok) {
        toast.success("Project updated!");
        setEditingId(null);
        fetchProjects();
      } else { toast.error(await res.text()); }
    } catch { toast.error("Error updating project."); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.info("Project deleted.");
        fetchProjects();
      } else { toast.error(await res.text()); }
    } catch { toast.error("Error deleting project."); }
  };

  const initials = (name) => name ? name.slice(0, 2).toUpperCase() : "??";

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.key && p.key.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      <style>{S}</style>
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
        <AdminNavbar />
      </div>

      <div className="pj-wrap">
        {/* Banner */}
        <div className="pj-banner">
          <div className="pj-banner-title">Project Management</div>
          <div className="pj-banner-sub">Create and manage all your projects from one place</div>
          <div className="pj-stats-row">
            <div className="pj-stat-pill">
              <span className="pj-stat-dot" />
              {projects.length} Projects
            </div>
            {search && (
              <div className="pj-stat-pill">
                <span className="pj-stat-dot" style={{ background: "#fbbf24" }} />
                {filtered.length} Results
              </div>
            )}
          </div>
        </div>

        <div className="pj-body">
          {/* Action Bar */}
          <div className="pj-actionbar">
            <div className="pj-search-wrap">
              <FaSearch className="pj-search-icon" />
              <input
                className="pj-search"
                placeholder="Search projects or keys…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="pj-add-row">
              <input
                className="pj-new-input"
                placeholder="Project name…"
                value={newProject}
                onChange={e => setNewProject(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleAdd()}
              />
              <input
                className="pj-new-input"
                placeholder="Key (e.g. HRMS)…"
                value={newProjectKey}
                onChange={e => setNewProjectKey(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleAdd()}
                style={{ width: "150px" }}
              />
              <button className="pj-btn-add" onClick={handleAdd}>
                <FaPlus size={11} /> Add
              </button>
            </div>
          </div>

          {/* Grid */}
          <div className="pj-section-label">All Projects</div>
          <div className="pj-grid">
            {filtered.length === 0 && (
              <div className="pj-empty">
                <div className="pj-empty-emoji">📁</div>
                <div className="pj-empty-txt">No projects found</div>
                <div className="pj-empty-sub">Try a different search or add one above</div>
              </div>
            )}
            {filtered.map((p) => (
              <div key={p.id} className={`pj-card${editingId === p.id ? " editing" : ""}`}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div className="pj-card-icon">{initials(p.name)}</div>
                  {editingId === p.id ? (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input
                        className="pj-card-edit-input"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        placeholder="Project name"
                        onKeyDown={e => { if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                      />
                      <input
                        className="pj-card-edit-input"
                        value={editKey}
                        onChange={e => setEditKey(e.target.value)}
                        placeholder="Project key"
                        onKeyDown={e => {
                          if (e.key === "Enter") handleUpdate(p.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="pj-card-name" title={p.name}>{p.name}</div>
                      <span className="pj-card-key">{p.key}</span>
                    </div>
                  )}
                </div>
                <div className="pj-card-footer">
                  {editingId === p.id ? (
                    <>
                      <button className="pj-card-btn save-btn" onClick={() => handleUpdate(p.id)}>
                        <FaSave size={10} /> Save
                      </button>
                      <button className="pj-card-btn cancel-btn" onClick={() => {
                        setEditingId(null);
                        setEditName("");
                        setEditKey("");
                      }}>
                        <FaTimes size={10} /> Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="pj-card-btn edit-btn"
                        onClick={() => { setEditingId(p.id); setEditName(p.name || ""); setEditKey(p.key || ""); }}>
                        <FaEdit size={10} /> Edit
                      </button>
                      <button className="pj-card-btn del-btn" onClick={() => handleDelete(p.id)}>
                        <FaTrash size={10} /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}